import { isWorkflowFinished } from "./constants";
import type { ProjectSummary } from "./types";

export type PreviewSection = {
  title: string;
  paragraphs: string[];
};

export type RecordPreviewRow = {
  itemName: string;
  subitemName: string;
  sampleCount: string;
  sampledLocations: string;
  findings: string;
  decision: string;
};

function compact(values: Array<string | null | undefined>): string[] {
  return values.map((value) => String(value || "").trim()).filter(Boolean);
}

function hasRecognizedValue(value: string | null | undefined): boolean {
  return String(value || "").trim().length > 0;
}

function isNotInvolvedMarker(value: string | null | undefined): boolean {
  return String(value || "").trim() === "/";
}

function hasEffectiveValue(value: string | null | undefined): boolean {
  return hasRecognizedValue(value) && !isNotInvolvedMarker(value);
}

function buildConstructionNature(project: ProjectSummary): string {
  if (!project.basicInfo.constructionNature.length) {
    return "建设性质待补充";
  }
  return `${project.basicInfo.constructionNature.join("、")}项目`;
}

function formatAreaValue(value: string | null | undefined): string {
  if (!hasEffectiveValue(value)) {
    return "";
  }
  return `${String(value).trim()}㎡`;
}

function formatHeightValue(value: string | null | undefined): string {
  if (!hasEffectiveValue(value)) {
    return "";
  }
  return `${String(value).trim()}m`;
}

function getDeclarationTypeSentence(project: ProjectSummary): string {
  const projectCategory = String(project.basicInfo.projectCategory || "").trim();
  if (projectCategory === "特殊建设工程") {
    return "本工程属于特殊建设工程";
  }
  if (projectCategory === "其他建设工程") {
    return "本工程属于其他建设工程";
  }
  return "本工程属于建设工程消防验收项目";
}

function buildBuildingUnitParagraph(unit: ProjectSummary["overview"]["buildingUnits"][number]): string {
  return compact([
    `${unit.buildingName || "建筑单体"}`,
    unit.usageType ? `使用性质为${unit.usageType}` : "",
    hasRecognizedValue(unit.aboveGroundFloorCount) ? `地上${unit.aboveGroundFloorCount}层` : "",
    hasRecognizedValue(unit.undergroundFloorCount) ? `地下${unit.undergroundFloorCount}层` : "",
    hasEffectiveValue(unit.aboveGroundBuildingArea) ? `地上建筑面积${formatAreaValue(unit.aboveGroundBuildingArea)}` : "",
    hasEffectiveValue(unit.undergroundBuildingArea) ? `地下建筑面积${formatAreaValue(unit.undergroundBuildingArea)}` : "",
    hasEffectiveValue(unit.buildingHeight) ? `建筑高度${formatHeightValue(unit.buildingHeight)}` : "",
    unit.structureType ? `结构类型为${unit.structureType}` : "",
    unit.fireResistanceLevel ? `耐火等级为${unit.fireResistanceLevel}` : "",
  ]).join("，") + "。";
}

function buildProjectOverviewSection(project: ProjectSummary): PreviewSection {
  const paragraphs = [
    `${getDeclarationTypeSentence(project)}${project.basicInfo.projectAddress ? `，位于${project.basicInfo.projectAddress}` : ""}。该项目为${buildConstructionNature(project)}。`,
    ...project.overview.buildingUnits
      .filter((unit) => compact([unit.buildingName, unit.usageType, unit.structureType]).length > 0)
      .map(buildBuildingUnitParagraph),
  ];

  if (isNotInvolvedMarker(project.overview.decorationPart)) {
    paragraphs.push("装饰装修：不涉及。");
  } else if (
    hasRecognizedValue(project.overview.decorationPart) ||
    hasRecognizedValue(project.overview.decorationArea) ||
    hasRecognizedValue(project.overview.decorationFloors)
  ) {
    paragraphs.push(
      compact([
        "装修装饰",
        !isNotInvolvedMarker(project.overview.decorationPart) && hasRecognizedValue(project.overview.decorationPart)
          ? `装修部位为${project.overview.decorationPart}`
          : "",
        hasEffectiveValue(project.overview.decorationArea)
          ? `装修面积${formatAreaValue(project.overview.decorationArea)}`
          : "",
        !isNotInvolvedMarker(project.overview.decorationFloors) && hasRecognizedValue(project.overview.decorationFloors)
          ? `装修所在层数${project.overview.decorationFloors}`
          : "",
      ]).join("，") + "。",
    );
  }

  if (isNotInvolvedMarker(project.overview.insulationMaterialCategory)) {
    paragraphs.push("建筑保温：不涉及。");
  } else if (
    hasRecognizedValue(project.overview.insulationMaterialCategory) ||
    hasRecognizedValue(project.overview.insulationFloors) ||
    hasRecognizedValue(project.overview.insulationPart) ||
    hasRecognizedValue(project.overview.insulationMaterial)
  ) {
    paragraphs.push(
      compact([
        "建筑保温",
        !isNotInvolvedMarker(project.overview.insulationMaterialCategory) && hasRecognizedValue(project.overview.insulationMaterialCategory)
          ? `材料类别为${project.overview.insulationMaterialCategory}`
          : "",
        !isNotInvolvedMarker(project.overview.insulationFloors) && hasRecognizedValue(project.overview.insulationFloors)
          ? `保温所在层数${project.overview.insulationFloors}`
          : "",
        !isNotInvolvedMarker(project.overview.insulationPart) && hasRecognizedValue(project.overview.insulationPart)
          ? `保温部位为${project.overview.insulationPart}`
          : "",
        !isNotInvolvedMarker(project.overview.insulationMaterial) && hasRecognizedValue(project.overview.insulationMaterial)
          ? `保温材料为${project.overview.insulationMaterial}`
          : "",
      ]).join("，") + "。",
    );
  }

  paragraphs.push(
    project.overview.fireFacilitySystems.length
      ? `本工程设有${project.overview.fireFacilitySystems.join("、")}等消防设施。`
      : "本工程设有xxxxxxxx等消防设施。",
  );

  return {
    title: "一、项目概况",
    paragraphs,
  };
}

function isLegacyGeneratedProjectOverviewParagraphs(paragraphs: string[]): boolean {
  const normalized = paragraphs.map((paragraph) => String(paragraph || "").trim()).filter(Boolean);
  return (
    normalized.length >= 2 &&
    normalized[0].startsWith("本工程属于") &&
    normalized[0].endsWith("。") &&
    normalized[1].includes("项目名称为") &&
    normalized[1].includes("位于") &&
    normalized[1].includes("该项目为")
  );
}

function buildReceivingSection(project: ProjectSummary): PreviewSection {
  return {
    title: "二、接件",
    paragraphs: [project.receiving.documentBody || "接件正文待补充。"],
  };
}

function buildConclusionSection(project: ProjectSummary): PreviewSection {
  const passCount = project.inspectionItems.filter((item) => item.decision === "pass").length;
  const failCount = project.inspectionItems.filter((item) => item.decision === "fail").length;
  const notInvolvedCount = project.inspectionItems.filter((item) => item.decision === "not_involved").length;
  const pendingCount = project.inspectionItems.filter((item) => !isWorkflowFinished(item.workflowStatus)).length;

  return {
    title: "三、现场评定及结论",
    paragraphs: [
      `当前共有 ${project.inspectionItems.length} 个单项，其中合格 ${passCount} 项，不合格 ${failCount} 项，不涉及 ${notInvolvedCount} 项，未完成 ${pendingCount} 项。`,
      pendingCount === 0
        ? "当前现场评定信息已具备继续整理报告正文的基础。"
        : "当前仍有单项未完成，预览仅作为阶段性草稿使用。",
    ],
  };
}

function buildAttachmentParagraphs(project: ProjectSummary): string[] {
  const paragraphs: string[] = [];
  const numerals = ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二", "十三", "十四", "十五", "十六", "十七", "十八"];

  for (const [itemIndex, item] of project.inspectionItems.entries()) {
    const numeral = numerals[itemIndex] || String(itemIndex + 1);
    if (item.decision === "not_involved") {
      paragraphs.push(`（${numeral}）${item.itemName}（不涉及）`);
      continue;
    }

    paragraphs.push(`（${numeral}）${item.itemName}`);

    for (let index = 0; index < item.subitems.length; index += 1) {
      const subitem = item.subitems[index];
      if (subitem.decision === "not_involved") {
        paragraphs.push(`${index + 1}、${subitem.subitemName}（不涉及）`);
        continue;
      }

      paragraphs.push(`${index + 1}、${subitem.subitemName}`);
      paragraphs.push(`抽查数量：${subitem.sampleCount || "/"}`);
      paragraphs.push(`抽查部位：${subitem.sampledLocations || "/"}`);
      if (subitem.photos.length) {
        paragraphs.push(`检查照片：已插入 ${subitem.photos.length} 张。`);
      }
      paragraphs.push(`抽查情况：${subitem.findings || "待补充。"}`);
    }
  }

  return paragraphs.length ? paragraphs : ["附件内容待补充。"];
}

function buildAttachmentSection(project: ProjectSummary): PreviewSection {
  return {
    title: "附件：现场评定内容预览",
    paragraphs: buildAttachmentParagraphs(project),
  };
}

export function buildReportPreview(project: ProjectSummary): PreviewSection[] {
  return [
    buildProjectOverviewSection(project),
    buildReceivingSection(project),
    buildConclusionSection(project),
    buildAttachmentSection(project),
  ];
}

export function buildRecordPreviewRows(project: ProjectSummary): RecordPreviewRow[] {
  return project.inspectionItems.flatMap((item) =>
    item.subitems.map((subitem) => ({
      itemName: item.itemName,
      subitemName: subitem.subitemName,
      sampleCount: subitem.decision === "not_involved" ? "/" : subitem.sampleCount,
      sampledLocations: subitem.decision === "not_involved" ? "/" : subitem.sampledLocations,
      findings: subitem.decision === "not_involved" ? "/" : subitem.findings,
      decision:
        subitem.decision === "pass"
          ? "合格"
          : subitem.decision === "fail"
            ? "不合格"
            : subitem.decision === "not_involved"
              ? "不涉及"
              : "待填写",
    })),
  );
}
